export const GLOBAL_DEFAULT_LINK_IMAGE_PATH = 'image_link/default_image.png';
export const GLOBAL_DEFAULT_AVATAR_PATH = 'default_avatars/default_avatar.png';

export const getStoragePathFromDownloadUrl = (downloadUrl) => {
	if (typeof downloadUrl !== 'string' || !downloadUrl.trim()) {
		return null;
	}
	try {
		const match = new URL(downloadUrl).pathname.match(/\/v0\/b\/[^/]+\/o\/(.+)$/);
		if (!match?.[1]) return null;
		return decodeURIComponent(match[1]);
	} catch {
		return null;
	}
};

export const isGlobalDefaultAvatarPath = (storagePath) => (
	storagePath === GLOBAL_DEFAULT_AVATAR_PATH
);

export const sanitizeStorageFileName = (name) => {
	const sanitized = String(name ?? '')
		.toLowerCase()
		.replace(/\s+/g, '_')
		.replace(/[^a-z0-9_.-]/g, '_')
		.replace(/_+/g, '_')
		.replace(/^[_.]+|[_.]+$/g, '')
		.slice(0, 100);
	return sanitized || 'link';
};

export const getExtensionFromFile = (file) => {
	if (file?.name && /\.[a-z0-9]+$/i.test(file.name)) {
		return file.name.split('.').pop().toLowerCase();
	}
	const mimeMap = {
		'image/jpeg': 'jpg',
		'image/png': 'png',
		'image/webp': 'webp',
	};
	return mimeMap[file?.type] ?? 'png';
};

export const getExtensionFromPath = (storagePath) => {
	const fileName = String(storagePath ?? '').split('/').pop() ?? '';
	const match = fileName.match(/\.([a-z0-9]+)$/i);
	return match?.[1]?.toLowerCase() ?? 'png';
};

export const buildLinkImageStoragePath = (userId, linkName, file, timestamp = Date.now()) => {
	const sanitized = sanitizeStorageFileName(linkName);
	const ext = getExtensionFromFile(file);
	return `${userId}/image_link/${sanitized}_${timestamp}.${ext}`;
};

export const buildAvatarStoragePath = (userId, file, timestamp = Date.now()) => {
	const ext = getExtensionFromFile(file);
	return `${userId}/avatar/${timestamp}.${ext}`;
};

export const getUserLinkImagesPath = (userId) => `${userId}/image_link`;

export const getUserAvatarPath = (userId) => `${userId}/avatar`;

export const getUserStorageRoot = (userId) => userId;

export const isGlobalDefaultLinkImagePath = (storagePath) => (
	storagePath === GLOBAL_DEFAULT_LINK_IMAGE_PATH
);

export const isLegacyLinkImagePath = (storagePath) => (
	Boolean(storagePath)
	&& storagePath.startsWith('image_link/')
	&& storagePath !== GLOBAL_DEFAULT_LINK_IMAGE_PATH
);

export const isLegacyAvatarPath = (storagePath, userId) => (
	Boolean(storagePath)
	&& Boolean(userId)
	&& storagePath.startsWith(`user_avatars/${userId}/`)
);

export const isUserScopedLinkImagePath = (storagePath, userId) => (
	Boolean(storagePath)
	&& Boolean(userId)
	&& storagePath.startsWith(`${userId}/image_link/`)
);

export const isUserScopedAvatarPath = (storagePath, userId) => (
	Boolean(storagePath)
	&& Boolean(userId)
	&& storagePath.startsWith(`${userId}/avatar/`)
);

export const canRemoveAvatarUrl = (downloadUrl, userId) => {
	const storagePath = getStoragePathFromDownloadUrl(downloadUrl);
	if (!storagePath || !userId) return false;
	if (isGlobalDefaultAvatarPath(storagePath)) return false;
	return isUserScopedAvatarPath(storagePath, userId) || isLegacyAvatarPath(storagePath, userId);
};

export const needsLinkImageMigration = (imageUrl, userId) => {
	const storagePath = getStoragePathFromDownloadUrl(imageUrl);
	if (!storagePath || !userId) return false;
	if (isGlobalDefaultLinkImagePath(storagePath)) return false;
	if (isUserScopedLinkImagePath(storagePath, userId)) return false;
	return isLegacyLinkImagePath(storagePath);
};

export const needsAvatarMigration = (photoURL, userId) => {
	const storagePath = getStoragePathFromDownloadUrl(photoURL);
	if (!storagePath || !userId) return false;
	if (isUserScopedAvatarPath(storagePath, userId)) return false;
	return isLegacyAvatarPath(storagePath, userId);
};
